import { Fragment, useState } from 'react';
import { api } from '../../lib/api.js';
import { useFetch } from '../../hooks/useFetch.js';
import { Seo } from '../../components/Seo.jsx';
import { AdminTabs } from '../../components/AdminTabs.jsx';
import { Button, Badge, Alert, Spinner, SectionTitle, EmptyState } from '../../components/ui.jsx';

const TONE = { ok: 'positive', degraded: 'caution', failing: 'negative' };
const OUTCOME_TONE = {
  recorded: 'positive',
  duplicate: 'neutral',
  ignored: 'neutral',
  rejected: 'caution',
  error: 'negative',
};
const OUTCOMES = ['recorded', 'duplicate', 'ignored', 'rejected', 'error'];
const CHANNELS = [
  'click',
  'postback',
  'pixel',
  'stripe_hook',
  'shopify_hook',
  'woocommerce_hook',
  'shopify_app_hook',
  'stripe_connect',
];

const when = (d) => (d ? new Date(d).toLocaleString() : '—');

/**
 * Tracking health, for operators. What the monitor last concluded, how the
 * end-to-end probe did step by step, what is arriving, and every recent
 * delivery that failed — each openable by its trace id.
 */
export default function Health() {
  const { data, loading, reload } = useFetch('/admin/health');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);

  async function runNow() {
    setRunning(true);
    setError('');
    try {
      await api.post('/admin/health/run');
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  }

  if (loading) return <Spinner />;
  const latest = data?.latest;
  const status = latest?.status ?? data?.state?.status ?? null;

  return (
    <div className="mx-auto max-w-5xl space-y-8 px-4 py-10 sm:px-6">
      <Seo title="Tracking health" noindex />
      <AdminTabs />

      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tracking health</h1>
          <p className="mt-1 text-sm text-ink-muted">
            {latest
              ? `Last checked ${when(latest.at)}${latest.manual ? ' (by hand)' : ''}`
              : 'The monitor has not run yet. It starts a few seconds after boot in production.'}
            {data?.state?.since && ` · ${data.state.status} since ${when(data.state.since)}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {status && <Badge tone={TONE[status]}>{status}</Badge>}
          <Button onClick={runNow} disabled={running}>
            {running ? 'Checking…' : 'Run check now'}
          </Button>
        </div>
      </header>

      <Alert>{error}</Alert>

      {latest && (
        <section>
          <SectionTitle>Checks</SectionTitle>
          <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
            <table className="data-table">
              <tbody>
                {latest.checks.map((c) => (
                  <tr key={c.key}>
                    <td className="w-48 font-medium">{c.key}</td>
                    <td className="w-28">
                      <Badge tone={TONE[c.status]}>{c.status}</Badge>
                    </td>
                    <td className="text-ink-muted">{c.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {latest?.probe && (
        <section>
          <SectionTitle hint="A real click and real signed sales through every intake, on the public URL">
            End-to-end probe
          </SectionTitle>
          {!latest.probe.ok && (
            <Alert className="mb-3">
              Failed at {latest.probe.failedStep}: {latest.probe.error}
            </Alert>
          )}
          <ol className="flex flex-wrap gap-2">
            {latest.probe.steps.map((s) => (
              <li
                key={s.name}
                title={s.detail || undefined}
                className="rounded-md border border-rule bg-paper-raised px-3 py-2 text-sm"
              >
                <Badge tone={s.ok ? 'positive' : 'negative'}>{s.ok ? 'pass' : 'fail'}</Badge>{' '}
                <span className="font-medium">{s.name}</span>{' '}
                <span className="tabular-nums text-ink-faint">{s.ms} ms</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section>
        <SectionTitle hint="Real traffic only — the probe's own requests are left out">
          Deliveries
        </SectionTitle>
        <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
          <table className="data-table">
            <thead>
              <tr>
                <th>Channel</th>
                {OUTCOMES.map((o) => (
                  <th key={o} className="num">
                    {o}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CHANNELS.map((ch) => (
                <tr key={ch}>
                  <td className="font-medium">{ch}</td>
                  {OUTCOMES.map((o) => {
                    const hour = data?.deliveries?.lastHour?.[ch]?.[o] ?? 0;
                    const day = data?.deliveries?.lastDay?.[ch]?.[o] ?? 0;
                    return (
                      <td key={o} className="num tabular-nums">
                        {day ? (
                          <>
                            {hour} <span className="text-ink-faint">/ {day}</span>
                          </>
                        ) : (
                          <span className="text-ink-faint">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-1.5 text-xs text-ink-faint">
          Last hour / last 24 hours. Successful clicks are counted on Clicks, not here.
        </p>
      </section>

      <section>
        <SectionTitle hint="Rejected and failed deliveries, newest first. Click one to open it.">
          Recent failures
        </SectionTitle>
        {data?.failures?.length ? (
          <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Channel</th>
                  <th>Result</th>
                  <th>Program</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.failures.map((f) => (
                  <Fragment key={f.traceId}>
                    <tr
                      className="cursor-pointer hover:bg-paper-sunken"
                      onClick={() => setOpen(open === f.traceId ? null : f.traceId)}
                    >
                      <td className="whitespace-nowrap text-ink-muted">{when(f.at)}</td>
                      <td>{f.channel}</td>
                      <td>
                        <Badge tone={OUTCOME_TONE[f.outcome]}>{f.outcome}</Badge>
                        <span className="ml-1 text-xs text-ink-faint">{f.httpStatus}</span>
                      </td>
                      <td>{f.program?.title ?? <span className="text-ink-faint">—</span>}</td>
                      <td className="text-sm">{f.reason ?? '—'}</td>
                    </tr>
                    {open === f.traceId && (
                      <tr>
                        <td colSpan={5}>
                          <TraceDetail traceId={f.traceId} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="Nothing has failed">
            No rejected or failed deliveries in the last 30 days.
          </EmptyState>
        )}
      </section>
    </div>
  );
}

function TraceDetail({ traceId }) {
  const { data, loading, error } = useFetch(`/admin/traces/${traceId}`);
  if (loading) return <Spinner />;
  if (error) return <Alert>{error.message ?? String(error)}</Alert>;
  return (
    <pre className="max-h-80 overflow-auto rounded bg-paper-sunken p-3 text-xs">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}
